import { config } from '@/config/env'
import { getAccessToken } from '@/auth/supabaseClient'
import type { ItemType, MarketPriceInfo, WalletInfo, TradeResult, SellResult, CraftResult, ExchangeResult } from './types'

function uuid(): string {
  return crypto.randomUUID()
}

async function authFetch(path: string, options: RequestInit = {}): Promise<Response> {
  const token = await getAccessToken()
  if (!token) throw new Error('Not authenticated')
  const res = await fetch(`${config.apiBaseUrl}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    },
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new Error(body.error || body.message || `Request failed: ${res.status}`)
  }
  return res
}

export async function registerPlayer(): Promise<WalletInfo> {
  const res = await authFetch('/economy/register', { method: 'POST' })
  return res.json()
}

export async function getWallet(): Promise<WalletInfo> {
  const res = await authFetch('/economy/wallet')
  return res.json()
}

export async function getMarketPrices(villageId: number): Promise<MarketPriceInfo[]> {
  const res = await authFetch(`/economy/market/${villageId}`)
  return res.json()
}

export async function buyFromMarket(
  villageId: number,
  item: ItemType,
  quantity: number,
  maxPrice: number,
): Promise<TradeResult> {
  const res = await authFetch('/economy/buy', {
    method: 'POST',
    body: JSON.stringify({
      tx_id: uuid(),
      village_id: villageId,
      item,
      quantity,
      max_price: maxPrice,
    }),
  })
  return res.json()
}

export async function sellToMarket(
  villageId: number,
  item: ItemType,
  quantity: number,
  minPrice: number = 0,
): Promise<SellResult> {
  const res = await authFetch('/economy/sell', {
    method: 'POST',
    body: JSON.stringify({
      tx_id: uuid(),
      village_id: villageId,
      item,
      quantity,
      min_price: minPrice,
    }),
  })
  return res.json()
}

export async function craftItem(
  outputItem: ItemType,
  multiplier: number = 1,
): Promise<CraftResult> {
  const res = await authFetch('/economy/craft', {
    method: 'POST',
    body: JSON.stringify({
      tx_id: uuid(),
      output_item: outputItem,
      multiplier,
    }),
  })
  return res.json()
}

export async function healAtChurch(cost: number): Promise<WalletInfo> {
  const res = await authFetch('/economy/heal', {
    method: 'POST',
    body: JSON.stringify({ tx_id: uuid(), cost }),
  })
  return res.json()
}

export async function collectResource(
  item: ItemType,
  quantity: number,
): Promise<WalletInfo> {
  const res = await authFetch('/economy/collect', {
    method: 'POST',
    body: JSON.stringify({ tx_id: uuid(), item, quantity }),
  })
  return res.json()
}

export async function directBuy(
  villageId: number,
  item: ItemType,
  quantity: number,
  priceModifier: number,
): Promise<TradeResult> {
  const res = await authFetch('/economy/direct-buy', {
    method: 'POST',
    body: JSON.stringify({
      tx_id: uuid(),
      village_id: villageId,
      item,
      quantity,
      price_modifier: priceModifier,
    }),
  })
  return res.json()
}

export async function exchangeCurrency(
  toSolidus: boolean,
  amount: number,
): Promise<ExchangeResult> {
  const res = await authFetch('/economy/exchange', {
    method: 'POST',
    body: JSON.stringify({
      tx_id: uuid(),
      to_solidus: toSolidus,
      amount,
    }),
  })
  return res.json()
}
